import { ChartInsight } from '@toggle/toggle';
import { addMinutes, differenceInMilliseconds, startOfMinute } from 'date-fns';

import { PriceDisplay, YAxisType } from '~/types/axis.types';
import {
  BaseChartAPIProps,
  CreateChartOptions,
  CreateChartOptionsWithColors,
  CreateChartOptionsWithConfig,
} from '~/types/create.types';
import { ResampleIntervals } from '~/types/resample.types';
import { SeriesType } from '~/types/series.types';
import { ChartAssetData } from '~/types/timeseries.types';
import { COLLAPSED_PANE_HEIGHT, PANE_MAX_COUNT } from '~/utils/constants';
import { createCanvasContext } from '~/utils/createCanvasContext';
import { getPercentage } from '~/utils/y-scale/y-scale';

import { drawCrosshair } from '../crosshair/crosshair';
import { dispatchInsightsInDomainEvent } from '../events/events';
import { reDraw } from './redraw/redraw';
import { InitProps, setup } from './setup/setup';

interface ChartAPIProps {
  canvasElement: HTMLCanvasElement;
  options: CreateChartOptions;
}

export interface AddPaneProps {
  data: ChartAssetData[];
  priceDisplay: PriceDisplay;
  yAxisType: YAxisType;
  seriesType: SeriesType;
}

export interface ChartAPIReturn {
  init: (props: InitProps) => void;
  displayInsights: (insights: ChartInsight[]) => void;
  getBaseData: () => BaseChartAPIProps | undefined;
  removeListeners: () => void;
  reset: () => void;
  updateLastPrice: (price: number, timestamp?: string) => void;
}

const getNextBoundaryUTC = (date: Date, interval: ResampleIntervals): Date => {
  if (interval !== '1m') {
    return date;
  }

  return startOfMinute(addMinutes(date, 1));
};

const drawLiveCrosshair = (base: BaseChartAPIProps) => {
  const primaryPane = base.panes.find(p => p.id === base.primaryPaneId);

  if (!primaryPane) {
    return;
  }

  const primaryAsset = primaryPane.chartAssetData[0];

  if (!primaryAsset || primaryAsset.ts.length === 0) {
    return;
  }

  const lastIdx = primaryAsset.ts.length - 1;
  const lastBar = primaryAsset.ts[lastIdx];
  const isMerged = primaryPane.yAxisType === 'merged';
  const axisIndex = primaryPane.y.findIndex(
    yAxis => yAxis.entityDefaultSnake === primaryAsset.entity.default_snake
  );

  if (axisIndex === -1) {
    return;
  }

  const yAxis = primaryPane.y[axisIndex];
  const priceDisplay: PriceDisplay = isMerged ? 'percentage' : 'price';
  const baseValue =
    primaryPane.domainTimeSeries[axisIndex]?.start.close ??
    primaryAsset.ts[0].close;
  const value =
    priceDisplay === 'price'
      ? lastBar.close
      : getPercentage(lastBar.close, baseValue);
  const yCoord = yAxis.yScale(value);
  const previousClose =
    lastIdx > 0 ? primaryAsset.ts[lastIdx - 1].close : lastBar.close;
  const isBullish = lastBar.close >= previousClose;
  const lineColor = isBullish
    ? base.options.colors.bars.up.value
    : base.options.colors.bars.down.value;
  const backgroundColor = isBullish
    ? base.options.colors.threshold.bullish.value
    : base.options.colors.threshold.bearish.value;
  const circleColor = lineColor;

  let subtitle;

  if (base.resampleInterval === '1m') {
    const nextBoundaryFromBar = getNextBoundaryUTC(
      new Date(lastBar.time),
      base.resampleInterval
    );
    const remainingMs = Math.max(
      0,
      differenceInMilliseconds(nextBoundaryFromBar, new Date())
    );
    const secs = Math.max(0, Math.ceil(remainingMs / 1000));
    subtitle = `0:${secs.toString().padStart(2, '0')}`;
  }

  drawCrosshair({
    ...base,
    x1: base.x.xScale(lastIdx),
    drawVertical: false,
    date: new Date(lastBar.time),
    subtitle,
    hoveredPoints: [
      {
        value,
        circleColor,
        lineColor,
        backgroundColor,
        y: yAxis,
        yCoord,
        priceDisplay,
        shouldDrawCircle: true,
        yAxisSize: base.yAxisSizes[axisIndex],
      },
    ],
  });
};

const DEFAULT_CONFIG: CreateChartOptionsWithConfig['config'] = {
  enableZoom: true,
  enableHover: true,
  paneMaxCount: PANE_MAX_COUNT,
  collapsedPaneHeight: COLLAPSED_PANE_HEIGHT,
  hoverVariant: 'default',
  panePaddingTop: 0,
  panePaddingRight: 0,
};

export const chartAPI = ({
  canvasElement,
  options,
}: ChartAPIProps): null | ChartAPIReturn => {
  const context = createCanvasContext(canvasElement, options);
  let rafId: number | undefined;
  let lastCountdownLabel: string | undefined;

  if (!context) {
    return null;
  }

  const optionsWithConfig: CreateChartOptionsWithColors = {
    ...options,
    config: {
      ...DEFAULT_CONFIG,
      ...options.config,
    },
    colors: options.colors,
  };
  const chartSetup = setup(canvasElement, optionsWithConfig, context);

  const startLiveCountdown = () => {
    if (rafId) {
      return false;
    }

    const loop = () => {
      const base = chartSetup.chartStore.getState().base;

      if (!base) {
        stopLiveCountdown();
        return;
      }

      if (base.resampleInterval !== '1m') {
        stopLiveCountdown();
        return;
      }

      const last = base.primaryAsset.ts.at(-1);
      const nextBoundary =
        last && getNextBoundaryUTC(new Date(last.time), base.resampleInterval);
      const remainingMs = nextBoundary
        ? Math.max(0, differenceInMilliseconds(nextBoundary, new Date()))
        : 0;
      const secs = Math.max(0, Math.ceil(remainingMs / 1000));
      const label = `0:${secs.toString().padStart(2, '0')}`;

      if (label !== lastCountdownLabel) {
        lastCountdownLabel = label;
        reDraw({ chartStore: chartSetup.chartStore });
        drawLiveCrosshair(base);
      }

      rafId = window.requestAnimationFrame(loop);
    };

    rafId = window.requestAnimationFrame(loop);
    return true;
  };

  const stopLiveCountdown = () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = undefined;
      lastCountdownLabel = undefined;
    }
  };

  let countdownStarted = false;

  return {
    init: props => {
      chartSetup.initialiseData(props);
      chartSetup.setListeners(true);
    },
    displayInsights: insights => {
      const newBase = chartSetup.chartStore.getState().setInsights(insights);
      dispatchInsightsInDomainEvent(newBase);
    },
    getBaseData: () => {
      return chartSetup.chartStore.getState().base;
    },
    removeListeners: () => {
      chartSetup.setListeners(false);
      stopLiveCountdown();
    },
    reset: () => {
      chartSetup.setListeners(false);
      chartSetup.chartStore.setState({ base: undefined });
      stopLiveCountdown();
    },
    updateLastPrice: (price, timestamp) => {
      if (!countdownStarted) {
        countdownStarted = startLiveCountdown();
      }

      chartSetup.chartStore.getState().updateLastPrice(price, timestamp);
      reDraw({ chartStore: chartSetup.chartStore });

      const base = chartSetup.chartStore.getState().base;

      if (base) {
        drawLiveCrosshair(base);
      }
    },
  };
};
